import {
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useGetServicesQuery } from '../../../store/service/serviceSlice';
import useToggleList from '../hooks/useToggleList';
import { createIconFromName } from '../../../util/iconUtil';
import ErrorTypography from '../../../shared/ErrorTypography/ErrorTypography';
import { Service } from '../../../store/service/types';
import { Trans } from 'react-i18next';
import { nameTranslator } from '../../../locales/NameTranslator';
import i18n from '../../../locales/i18n';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { togglePauseTut } from '../../../store/tutorial/tutorialSlice';

type Props = {
  onClick: (service: Service) => void;
};

const ServicesList = ({ onClick }: Props): JSX.Element => {
  const { data, error, isLoading } = useGetServicesQuery(undefined);
  const { get, toggle } = useToggleList(false);
  const tutorial = useAppSelector(state => state.tutorial);
  const dispatch = useAppDispatch();

  const handleClick = (category: string) => () => {
    toggle(category);
    if(tutorial.active) {
      if (!tutorial.paused && tutorial.actionSteps.includes(tutorial.stepIndex)) {
        dispatch(togglePauseTut());
      }
    }
  }
  const currentLanguage = i18n.language;
  
  let menuEntries: JSX.Element[];

  if (error) {
    menuEntries = [
      <ListItem key={0}>
        <ListItemText>
          <ErrorTypography message={'Error fetching services.'} />
        </ListItemText>
      </ListItem>,
    ];
  } else if (!isLoading && data) {
    menuEntries = data.modules
      .map(({ name_de, name_en, icon, module }, i) => [
        <div className={module + '-module'}>
          <ListItemButton key={3 * i} onClick={handleClick(currentLanguage === 'de' ? name_de : name_en)}>
            <ListItemIcon>{createIconFromName(icon)}</ListItemIcon>
            <ListItemText primary={currentLanguage === 'de' ? name_de : name_en} />
            {get(currentLanguage === 'de' ? name_de : name_en) ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </div>,
        <Collapse key={3 * i + 1} in={get(currentLanguage === 'de' ? name_de : name_en)} timeout={'auto'} unmountOnExit>
          {data.services
            .filter(service => service.module === module)
            .map((service, i) => (
              <div className={service.service + '-service'}>
                <List key={i} component={'div'} disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={() => onClick(service)}>
                    <ListItemIcon>{createIconFromName(service.icon)}</ListItemIcon>
                    <ListItemText primary={nameTranslator(service)} />
                  </ListItemButton>
                </List>
              </div>
            ))}
        </Collapse>,
        <Divider key={3 * i + 2} />,
      ])
      .flat(1);
  } else {
    menuEntries = [
      <ListItem key={0}>
        <ListItemText>
          <LinearProgress />
        </ListItemText>
      </ListItem>,
    ];
  }

  return (
    <List
      sx={{ overflowY: 'scroll', height: '50vh' }}
      component={'nav'}
      subheader={
        <ListSubheader component="div" id="nested-list-subheader">
          <b>
            <Trans>Services</Trans>
          </b>
        </ListSubheader>
      }
    >
      {menuEntries}
    </List>
  );
};

export default ServicesList;
